import React, { useEffect, useState, useMemo } from "react";
import {
  Box,
  Typography,
  Button,
  Select,
  MenuItem,
  LinearProgress,
  Paper,
  Grid,
  Card,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Tooltip,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";

import PrimarySmallBlueButton from "../../components/customButtons/PrimarySmallBlueButton";
import CustomCardWithBorder from "../../components/CustomCardWithBorder";
import CustomSelectField from "../../components/customFields/CustomSelectField";

import {
  DashboardStudentExamData,
  getRealTestTestTypes,
  GetTestMasterIssEnroll,
} from "../testPrepDashboard/Api/DashboardApi";
import { useNavigate } from "react-router-dom";
import Loader from "../../../SAPPages/components/Loader/Loader";
import NoDataAvailableScreen from "../../components/screens/NoDataAvailableScreen";
import NoTestTypePurchaseScreen from "../../components/screens/NoTestTypePurchaseScreen";
import ProgressBarWithLabel from "./components/ProgressBarWithLabel";
import ViewAllBlueButton from "../../components/customButtons/ViewAllBlueButton";
import { useDispatch } from "react-redux";
import { setDefaultExamData } from "../../../redux/features/examTestTypeId";
import { authFetch } from "../../../api";
import {
  ExamAllocationDetails,
  TestivaDashboardStudentExamData,
} from "../testPrepDashboard/Api/TestivaApi";

/* ----------------------------------------------------------------
   ALLOCATION AGGREGATION HELPERS
   Turns the raw ExamAllocationDetails() response into:
     { moduleName: { totalAssigned, activatedCount, pendingCount, dates: {...} } }
----------------------------------------------------------------- */

const MODULE_ID_NAME_MAP = {
  0: "Mock",
  4: "Listening",
  5: "Reading",
  6: "Writing",
  7: "Speaking",
};

const MODULE_COLORS = {
  Mock: "#572575",
  Listening: "#007BFF",
  Reading: "#515DE8",
  Writing: "#DC9F52",
  Speaking: "#1D860D",
};

/* ----------------------------------------------------------------
   NEW: map a testTypeList entry (from CustomSelectField) to the
   matching categoryId used inside ExamAllocationDetails().

   testTypeList entries look like:
     { sl_no, test_type_name, exam_portal_id, sub_test_type_name,
       sub_exam_portal_id, ... }

   ExamAllocationDetails() response has:
     final.categories = {
       "1": { id: 1, name: "IELTS Academic", category_id: 1, exam_type_id: 4 },
       "2": { id: 2, name: "IELTS General",  category_id: 2, exam_type_id: 4 },
       "3": { id: 3, name: "PTE Academic",   category_id: 1, exam_type_id: 5 },
       "4": { id: 4, name: "PTE Core",       category_id: 2, exam_type_id: 5 },
     }
     final.history[i].categoryId -> matches the numeric key above (1|2|3|4)

   The mapping rule: a category matches a testType option when
     category.exam_type_id  === testType.exam_portal_id
     category.category_id   === testType.sub_exam_portal_id
----------------------------------------------------------------- */

const findCategoryIdForTestType = (categories, testTypeObj) => {
  if (!categories || !testTypeObj) return null;

  const match = Object.entries(categories).find(
    ([, cat]) =>
      cat?.exam_type_id === testTypeObj?.exam_portal_id &&
      cat?.category_id === testTypeObj?.sub_exam_portal_id,
  );

  return match ? Number(match[0]) : null;
};

/* ----------------------------------------------------------------
   NEW: given the raw allocation API response, return a copy whose
   final.history is filtered down to a single categoryId.
   Pass categoryId = null/undefined to keep everything as-is.
----------------------------------------------------------------- */

const filterAllocationDataByCategory = (apiResponse, categoryId) => {
  if (!apiResponse?.final) return apiResponse;
  if (categoryId === null || categoryId === undefined) {
    // No confident mapping found for the selected test type -> show nothing
    // rather than silently mixing every exam type together.
    return {
      ...apiResponse,
      final: { ...apiResponse.final, history: [] },
    };
  }

  const filteredHistory = (apiResponse.final.history || []).filter(
    (entry) => entry?.categoryId === categoryId,
  );

  return {
    ...apiResponse,
    final: { ...apiResponse.final, history: filteredHistory },
  };
};

const computeExamAllocationSummary = (apiResponse) => {
  const final = apiResponse?.final || {};
  const history = final.history || [];
  const moduleData = {};

  const ensureModule = (name) => {
    if (!moduleData[name]) {
      moduleData[name] = {
        name,
        totalAssigned: 0,
        activatedCount: 0,
        pendingCount: 0,
        dates: {}, // "YYYY-MM-DD" -> { status, items: [] }
      };
    }
    return moduleData[name];
  };

  history.forEach((entry) => {
    const date = entry.availableOn;
    const status = entry.status; // "pending" | "activated"

    (entry.consumed || []).forEach((c) => {
      (c.allocationsNamed || []).forEach((alloc) => {
        const moduleName = MODULE_ID_NAME_MAP[alloc.id] || alloc.label;
        const mod = ensureModule(moduleName);

        mod.totalAssigned += alloc.qty || 0;
        if (status === "activated") mod.activatedCount += alloc.qty || 0;
        else mod.pendingCount += alloc.qty || 0;

        if (!mod.dates[date]) mod.dates[date] = { status, items: [] };
        if (status === "activated") mod.dates[date].status = "activated";

        mod.dates[date].items.push({
          label: alloc.label,
          qty: alloc.qty,
          type: alloc.type,
        });
      });
    });
  });

  return moduleData;
};

/* ----------------------------------------------------------------
   CALENDAR MODAL (opens when a module card is clicked)
   FIX: all hooks (useState, useEffect, useMemo) now run
   unconditionally on every render. The `if (!moduleInfo) return null`
   guard is placed AFTER every hook call, never before.
----------------------------------------------------------------- */

const WEEK_DAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

const formatDateKey = (y, m, d) =>
  `${y}-${String(m + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;

const ExamAllocationCalendarModal = ({ open, onClose, moduleInfo }) => {
  const [viewDate, setViewDate] = useState(new Date());
  const [selectedDay, setSelectedDay] = useState(null);

  // Whenever a new module is selected, jump the calendar to its first
  // allocation date and clear any previously selected day.
  useEffect(() => {
    if (moduleInfo) {
      const firstDateKey = Object.keys(moduleInfo?.dates || {}).sort()[0];
      if (firstDateKey) setViewDate(new Date(firstDateKey));
    }
    setSelectedDay(null);
  }, [moduleInfo]);

  const year = viewDate.getFullYear();
  const month = viewDate.getMonth();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const firstDayOfWeek = new Date(year, month, 1).getDay();

  // useMemo always runs, regardless of whether moduleInfo is null,
  // so the hook order never changes between renders.
  const cells = useMemo(() => {
    const arr = [];
    for (let i = 0; i < firstDayOfWeek; i++) arr.push(null);
    for (let d = 1; d <= daysInMonth; d++) arr.push(d);
    return arr;
  }, [year, month, daysInMonth, firstDayOfWeek]);

  // Safe to bail out now — every hook above has already executed.
  if (!moduleInfo) return null;

  const statusColor = (status) =>
    status === "activated" ? "#1D860D" : "#DC9F52";

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Typography fontFamily="Inter" fontWeight={700} fontSize="18px">
          {moduleInfo.name} — {moduleInfo.totalAssigned} allocated
        </Typography>
        <IconButton onClick={onClose} size="small">
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          mb={2}
        >
          <IconButton
            onClick={() => setViewDate(new Date(year, month - 1, 1))}
            size="small"
          >
            <ChevronLeftIcon />
          </IconButton>
          <Typography fontFamily="Inter" fontWeight={600}>
            {viewDate.toLocaleString("default", {
              month: "long",
              year: "numeric",
            })}
          </Typography>
          <IconButton
            onClick={() => setViewDate(new Date(year, month + 1, 1))}
            size="small"
          >
            <ChevronRightIcon />
          </IconButton>
        </Box>

        <Grid container>
          {WEEK_DAYS.map((wd) => (
            <Grid item xs={12 / 7} key={wd} textAlign="center">
              <Typography fontSize="12px" fontWeight={600} color="#888">
                {wd}
              </Typography>
            </Grid>
          ))}
        </Grid>

        <Grid container>
          {cells.map((day, idx) => {
            if (!day)
              return <Grid item xs={12 / 7} key={idx} sx={{ height: 64 }} />;
            const dateKey = formatDateKey(year, month, day);
            const dayData = moduleInfo.dates?.[dateKey];

            return (
              <Grid item xs={12 / 7} key={idx}>
                <Tooltip
                  title={
                    dayData
                      ? dayData.items
                          .map((it) => `${it.label} x${it.qty}`)
                          .join(", ")
                      : ""
                  }
                >
                  <Box
                    onClick={() => dayData && setSelectedDay(dateKey)}
                    sx={{
                      height: 64,
                      m: "3px",
                      borderRadius: "8px",
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      cursor: dayData ? "pointer" : "default",
                      border: dayData
                        ? `1.5px solid ${statusColor(dayData.status)}`
                        : "1px solid #eee",
                      background: dayData
                        ? `${statusColor(dayData.status)}15`
                        : "transparent",
                    }}
                  >
                    <Typography
                      fontSize="13px"
                      fontWeight={dayData ? 700 : 400}
                    >
                      {day}
                    </Typography>
                    {dayData && (
                      <Box
                        sx={{
                          width: 6,
                          height: 6,
                          borderRadius: "50%",
                          background: statusColor(dayData.status),
                          mt: "2px",
                        }}
                      />
                    )}
                  </Box>
                </Tooltip>
              </Grid>
            );
          })}
        </Grid>

        {selectedDay && moduleInfo.dates[selectedDay] && (
          <Box mt={3} p={2} borderRadius="10px" sx={{ background: "#f7f7f9" }}>
            <Typography fontWeight={700} fontSize="14px" mb={1}>
              {selectedDay} — {moduleInfo.dates[selectedDay].status}
            </Typography>
            {moduleInfo.dates[selectedDay].items.map((it, i) => (
              <Typography key={i} fontSize="13px" color="#333">
                • {it.label} (x{it.qty})
              </Typography>
            ))}
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};

/* ----------------------------------------------------------------
   ALLOCATION GRID (module totals, clickable -> opens calendar)
   Receives an already-filtered allocationResponse (single test type).
----------------------------------------------------------------- */

const ExamAllocationGrid = ({ allocationResponse }) => {
  const moduleData = useMemo(
    () => computeExamAllocationSummary(allocationResponse),
    [allocationResponse],
  );

  const [selectedModule, setSelectedModule] = useState(null);
  const modules = Object.values(moduleData).sort((a, b) =>
    a.name.localeCompare(b.name),
  );

  if (modules.length === 0) return null;

  return (
    <>
      <Grid container spacing={3}>
        {modules.map((mod) => {
          const color = MODULE_COLORS[mod.name] || "#333";
          return (
            <Grid item xs={12} sm={6} md={2.4} key={mod.name}>
              <Card
                onClick={() => setSelectedModule(mod)}
                sx={{
                  cursor: "pointer",
                  borderRadius: "12px",
                  boxShadow: "0 2px 6px rgba(0,0,0,0.1)",
                  borderLeft: `4px solid ${color}`,
                  p: "18px",
                  textAlign: "center",
                  transition: "transform 0.15s ease",
                  "&:hover": {
                    transform: "translateY(-3px)",
                    boxShadow: "0 6px 14px rgba(0,0,0,0.15)",
                  },
                }}
              >
                <Typography
                  fontFamily="Inter"
                  fontWeight={700}
                  fontSize="22px"
                  color={color}
                >
                  {mod.totalAssigned}
                </Typography>
                <Typography
                  fontFamily="Inter"
                  fontWeight={600}
                  fontSize="14px"
                  color="#000"
                  mb={1}
                >
                  {mod.name}
                </Typography>
                <Box display="flex" justifyContent="center" gap={1}>
                  <Typography fontSize="12px" color="#1D860D">
                    {mod.activatedCount} Assigned
                  </Typography>
                  <Typography fontSize="12px" color="#DC9F52">
                    {mod.pendingCount} Upcoming
                  </Typography>
                </Box>
              </Card>
            </Grid>
          );
        })}
      </Grid>

      <ExamAllocationCalendarModal
        open={Boolean(selectedModule)}
        onClose={() => setSelectedModule(null)}
        moduleInfo={selectedModule}
      />
    </>
  );
};

/* ----------------------------------------------------------------
   MAIN COMPONENT
----------------------------------------------------------------- */

const PerformanceDashboard = () => {
  const [testTypeList, setTestTypeList] = useState([]);
  const [testType, setTestType] = useState(null);
  const userId = localStorage.getItem("userId");
  const firstName = localStorage.getItem("firstName");
  const lastName = localStorage.getItem("lastName");
  const navigate = useNavigate();
  const [dashBoardData, setDashBoardData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [testTypeData, setTestTYpeData] = useState(null);
  const [firstTimedataLoading, setFirstTimeDataLoading] = useState(true);

  // Allocation state
  const [allocationData, setAllocationData] = useState(null);
  const [allocationLoading, setAllocationLoading] = useState(true);

  const redirectionLink = "/test-prep/classes";
  const redirectionLink1 = "/test-prep/performace/purchase-test";
  const dispatch = useDispatch();
  const handleTestTypeChange = (e) => {
    const selected = e.target.value; // contains the whole object
    setTestType(selected);
  };

  const fetchTestivaDashboardData = async (testType) => {
    try {
      setLoading(true);
      const res = await TestivaDashboardStudentExamData(testType);
      setDashBoardData(res);
    } catch (err) {
      console.log(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (testType) {
      fetchTestivaDashboardData(testType);
    }
  }, [testType]);

  const fetchExamAllocationDetails = async () => {
    try {
      setAllocationLoading(true);
      const res = await ExamAllocationDetails();
      setAllocationData(res);
    } catch (err) {
      console.log(err);
      setAllocationData(null);
    } finally {
      setAllocationLoading(false);
    }
  };

  const fetchData = async () => {
    try {
      const data = await getRealTestTestTypes(userId);
      setTestTypeList(data?.data);
    } catch (err) {
      console.error("Could not fetch test types");
      setTestTypeList([]);
    }
  };

  useEffect(() => {
    fetchData();
    fetchExamAllocationDetails();
  }, []);

  useEffect(() => {
    setFirstTimeDataLoading(true);
    const fetchData = async () => {
      try {
        const data = await GetTestMasterIssEnroll(userId);
        setTestType(data?.data?.sl_no);
        setFirstTimeDataLoading(false);
      } catch (err) {
        console.error("Could not fetch test types");
        setTestType(null);
        setFirstTimeDataLoading(false);
      }
    };
    fetchData();
  }, []);

  useEffect(() => {
    const selectedOBJ = testTypeList?.find((i) => i.sl_no === testType);
    setTestTYpeData(selectedOBJ);
  }, [testType, testTypeList]);

  /* --------------------------------------------------------------
     NEW: figure out which allocation "categoryId" the currently
     selected test type (testTypeData) corresponds to, then filter
     the raw allocation response down to just that category before
     computing the summary / calendar / upcoming-exam counts.
  ----------------------------------------------------------------- */
  const selectedCategoryId = useMemo(
    () =>
      findCategoryIdForTestType(
        allocationData?.final?.categories,
        testTypeData,
      ),
    [allocationData, testTypeData],
  );

  const filteredAllocationData = useMemo(
    () => filterAllocationDataByCategory(allocationData, selectedCategoryId),
    [allocationData, selectedCategoryId],
  );

  // Compute allocation summary once per filtered allocation data change
  // (used by both the grid and the "Upcoming Exams" stat card)
  const allocationSummary = useMemo(
    () => computeExamAllocationSummary(filteredAllocationData),
    [filteredAllocationData],
  );

  const hasAllocationDataForSelection =
    Object.keys(allocationSummary).length > 0;

  const totalPendingAllocations = useMemo(
    () =>
      Object.values(allocationSummary).reduce(
        (sum, mod) => sum + mod.pendingCount,
        0,
      ),
    [allocationSummary],
  );

  const progressConfig = {
    Reading: {
      max: 5,
      color: "#515DE8",
      icon: "/newdashboard/performance/Group 1171284044.png",
    },
    Listening: {
      max: 5,
      color: "#007BFF",
      icon: "/newdashboard/performance/Group 1171284044 (1).png",
    },
    "Question Specific Speaking": {
      max: 28,
      color: "#C21FA8",
      icon: "/newdashboard/performance/Group 1171284044 (2).png",
    },
    "Question Specific Listening": {
      max: 28,
      color: "#C21FA8",
      icon: "/newdashboard/performance/Group 1171284044 (2).png",
    },
    "Question Specific Reading": {
      max: 5,
      color: "#515DE8",
      icon: "/newdashboard/performance/Group 1171284044.png",
    },
    "Question Specific Writing": {
      max: 28,
      color: "#DC9F52",
      icon: "/newdashboard/performance/Group 1171284044 (3).png",
    },
    Writing: {
      max: 28,
      color: "#DC9F52",
      icon: "/newdashboard/performance/Group 1171284044 (3).png",
    },
    Speaking: {
      max: 28,
      color: "#1D860D",
      icon: "/newdashboard/performance/Group 1171284044 (5).png",
    },
    Mock: {
      max: 28,
      color: "#572575",
      icon: "/newdashboard/performance/Group 1171284044 (4).png",
    },
  };

  const handleViewTestdetails = (data) => {
    localStorage.setItem("testDetails", JSON.stringify(data));
    const url = `/test-prep/performance/purchase-test`;
    window.open(url, "_blank"); // open in new tab
  };

  const secondRedirectionLink = "/test-prep/performance/purchase-test";

  // navigate to the test page
  const handleNavigateToTest = () => {
    dispatch(setDefaultExamData(testTypeData)); // Redux
    localStorage.setItem("defaultExamData", JSON.stringify(testTypeData));
    window.open("performance/my-exam", "_blank", "noreferrer");
  };

  const handlePurchaseTest = () => {
    window.open(secondRedirectionLink, "_blank", "noreferrer");
  };

  const selectedTestTypeLabel = testTypeData
    ? `${testTypeData.test_type_name}${
        testTypeData.sub_test_type_name
          ? ` (${testTypeData.sub_test_type_name})`
          : ""
      }`
    : "";

  return (
    <>
      {/* Header */}
      <Grid container spacing={2}>
        <Grid item xs={12} sm={9}>
          <Typography
            fontFamily="Inter"
            fontWeight={400}
            color="#000000"
            fontSize="24px"
          >
            My Performance
          </Typography>
        </Grid>

        <Grid item xs={12} sm={3} textAlign="end">
          <PrimarySmallBlueButton
            buttonTitle="Purchase Tests"
            startIcon={
              <img
                src="/newdashboard/performance/ion_stats-chart.png"
                style={{ width: 20 }}
              />
            }
            onClick={handlePurchaseTest}
          />
        </Grid>

        {/* load first time */}
        {firstTimedataLoading ? (
          <Grid item xs={12}>
            <Loader text="Loading Performance Details ...." />
          </Grid>
        ) : (
          !firstTimedataLoading &&
          !testType && (
            <Grid item xs={12} mx={{ xs: 1, sm: 2, md: 5 }} mt={2}>
              <NoDataAvailableScreen
                topImage={
                  <img src="/newdashboard/performance/20769812_Sandy_Bus-25_Single-04 1 (1).png" />
                }
                text="No Any Test Type Purchased Yet"
                subText="You haven't purchased any exam packages yet. Explore available class or test to unlock full course content, progress tracking, mock tests, and personalized study insights."
                buttonName="Explore Classes"
                secondButtonName="Purchase Tests"
                maxWidth="100%"
                redirectionLink={redirectionLink}
                secondRedirectionLink={secondRedirectionLink}
              />
            </Grid>
          )
        )}

        {/* Welcome Section */}
        {!firstTimedataLoading && testType && (
          <>
            <Grid item xs={12}>
              <CustomCardWithBorder boxShadow={true}>
                <Grid container spacing={2} padding={2}>
                  <Grid item xs={12} sm={9} md={9}>
                    <Typography
                      fontFamily="Inter"
                      fontWeight={400}
                      color="#000000"
                      fontSize="22px"
                    >
                      Welcome Back{" "}
                      <Box
                        component="span"
                        sx={{ color: "#572575", cursor: "default" }}
                        fontWeight={700}
                      >
                        {firstName} !
                      </Box>
                    </Typography>

                    <Typography
                      fontFamily="Inter"
                      fontWeight={500}
                      color="#555555"
                      fontSize="14px"
                      mb={2}
                    >
                      You're making great progress in your exam preparation.
                      Keep it up!
                    </Typography>
                  </Grid>

                  <Grid item xs={12} sm={3} md={3} sx={{ textAlign: "right" }}>
                    <CustomSelectField
                      color="#572575"
                      ExpandMoreIconColor="#572575"
                      dafualtValue="Select Test Type"
                      options={testTypeList}
                      getOptionLabel={(opt) =>
                        `${opt?.test_type_name}${
                          opt?.sub_test_type_name
                            ? ` ( ${opt.sub_test_type_name} )`
                            : ""
                        }`
                      }
                      getOptionValue={(opt) => opt?.sl_no}
                      value={testType}
                      onChange={handleTestTypeChange}
                    />
                  </Grid>

                  {/* Stats Grid */}
                  {loading ? (
                    <Grid item xs={12}>
                      <Loader text="Exam Details..." height="20vh" />
                    </Grid>
                  ) : (
                    <Grid item xs={12}>
                      <Grid
                        container
                        spacing={2}
                        alignItems="stretch"
                        wrap="nowrap"
                      >
                        {/* Assigned Exams */}
                        <Grid item xs={2.4}>
                          <Card
                            sx={{
                              borderRadius: "12px",
                              boxShadow: "0 2px 6px rgba(0,0,0,0.1)",
                              borderLeft: "3px solid #DA5DC7",
                              p: "20px",
                              textAlign: "center",
                              height: "100%",
                              minHeight: "108px",
                              display: "flex",
                              flexDirection: "column",
                              justifyContent: "center",
                            }}
                          >
                            <Typography
                              fontFamily="Inter"
                              fontWeight={700}
                              color="#9D0385"
                              fontSize="16px"
                              mb={1}
                            >
                              {dashBoardData?.stats?.assigned || 0} Exams
                            </Typography>

                            <Typography
                              fontFamily="Inter"
                              fontWeight={400}
                              color="#000000"
                              fontSize="14px"
                            >
                              Total Assigned Exam
                            </Typography>
                          </Card>
                        </Grid>

                        {/* Pending Exams */}
                        <Grid item xs={2.4}>
                          <Card
                            sx={{
                              borderRadius: "12px",
                              boxShadow: "0 2px 6px rgba(0,0,0,0.1)",
                              borderLeft: "3px solid #DA5DC7",
                              p: "20px",
                              textAlign: "center",
                              height: "100%",
                              minHeight: "108px",
                              display: "flex",
                              flexDirection: "column",
                              justifyContent: "center",
                            }}
                          >
                            <Typography
                              fontFamily="Inter"
                              fontWeight={700}
                              color="#9D0385"
                              fontSize="16px"
                              mb={1}
                            >
                              {Math.max(
                                0,
                                (dashBoardData?.assignedCount || 0) -
                                  (dashBoardData?.givenCount || 0),
                              )}{" "}
                              Exams
                            </Typography>

                            <Typography
                              fontFamily="Inter"
                              fontWeight={400}
                              color="#000000"
                              fontSize="14px"
                            >
                              Total Pending Exam
                            </Typography>
                          </Card>
                        </Grid>

                        {/* Overall Completion */}
                        <Grid item xs={2.4}>
                          <Card
                            sx={{
                              background:
                                "linear-gradient(90deg, #F2D8FF 0%, #E2EDFF 100%)",
                              p: "20px",
                              textAlign: "center",
                              borderRadius: "6px",
                              border: "2px solid #F9E3F673",
                              height: "100%",
                              minHeight: "108px",
                              display: "flex",
                              flexDirection: "column",
                              justifyContent: "center",
                            }}
                          >
                            <Typography
                              fontFamily="Inter"
                              fontWeight={700}
                              color="#9D0385"
                              fontSize="16px"
                              mb={1}
                            >
                              {dashBoardData?.completion || 0} %
                            </Typography>

                            <Typography
                              fontFamily="Inter"
                              fontWeight={400}
                              color="#000000"
                              fontSize="14px"
                            >
                              Overall Completion
                            </Typography>
                          </Card>
                        </Grid>

                        {/* Upcoming Exams — now scoped to the selected test type */}
                        <Grid item xs={2.4}>
                          <Card
                            sx={{
                              background:
                                "linear-gradient(90deg, #F2D8FF 0%, #E2EDFF 100%)",
                              p: "20px",
                              textAlign: "center",
                              borderRadius: "6px",
                              border: "2px solid #F9E3F673",
                              height: "100%",
                              minHeight: "108px",
                              display: "flex",
                              flexDirection: "column",
                              justifyContent: "center",
                            }}
                          >
                            <Typography
                              fontFamily="Inter"
                              fontWeight={700}
                              color="#9D0385"
                              fontSize="16px"
                              mb={1}
                            >
                              {allocationLoading
                                ? "…"
                                : totalPendingAllocations || 0}
                            </Typography>

                            <Typography
                              fontFamily="Inter"
                              fontWeight={400}
                              color="#000000"
                              fontSize="14px"
                            >
                              Upcoming Exams
                            </Typography>
                          </Card>
                        </Grid>

                        {/* Graduation Image */}
                        <Grid
                          item
                          xs={2.4}
                          display="flex"
                          justifyContent="center"
                          alignItems="center"
                        >
                          <Box
                            sx={{
                              width: "80px",
                              maxWidth: "100%",
                              display: "flex",
                              justifyContent: "center",
                            }}
                          >
                            <img
                              src="/newdashboard/performance/graduation-cap.png"
                              alt="Graduation"
                              style={{
                                width: "100%",
                                height: "auto",
                                display: "block",
                              }}
                            />
                          </Box>
                        </Grid>
                      </Grid>
                    </Grid>
                  )}
                </Grid>
              </CustomCardWithBorder>
            </Grid>

            {/* Exam Allocation Summary Grid + Calendar — scoped to selected test type */}
            <Grid item xs={12}>
              <CustomCardWithBorder boxShadow={true}>
                <Box p={2}>
                  <Typography
                    fontFamily="Inter"
                    fontWeight={600}
                    color="#572575"
                    fontSize="20px"
                    mb={2}
                  >
                    Exam Allocation Summary
                    {selectedTestTypeLabel ? ` — ${selectedTestTypeLabel}` : ""}
                  </Typography>

                  {allocationLoading ? (
                    <Loader
                      text="Loading Allocation Details..."
                      height="20vh"
                    />
                  ) : hasAllocationDataForSelection ? (
                    <ExamAllocationGrid
                      allocationResponse={filteredAllocationData}
                    />
                  ) : (
                    <Typography fontSize="14px" color="#888">
                      No allocation data available
                      {selectedTestTypeLabel
                        ? ` for ${selectedTestTypeLabel}`
                        : ""}
                      .
                    </Typography>
                  )}
                </Box>
              </CustomCardWithBorder>
            </Grid>

            {/* Exams Progress Bars Grid */}
            {loading ? (
              <Grid item xs={12}>
                <CustomCardWithBorder boxShadow={true}>
                  <Grid container spacing={3}>
                    <Grid
                      item
                      xs={12}
                      display="flex"
                      alignItems="center"
                      gap={1}
                      justifyContent="space-between"
                    >
                      <Box display="flex" alignItems="center" gap={1}>
                        <Box width={25}>
                          <img src="/newdashboard/performance/material-symbols-light_docs.png" />
                        </Box>
                        <Typography
                          fontFamily="Inter"
                          fontWeight={600}
                          color="#572575"
                          fontSize="20px"
                        >
                          My Exam
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12}>
                      <Loader text="Exam Details..." height="20vh" />
                    </Grid>
                  </Grid>
                </CustomCardWithBorder>
              </Grid>
            ) : !loading && dashBoardData?.finalData?.length > 0 ? (
              <Grid item xs={12}>
                <CustomCardWithBorder boxShadow={true}>
                  <Grid container spacing={3}>
                    <Grid
                      item
                      xs={12}
                      display="flex"
                      alignItems="center"
                      gap={1}
                      justifyContent="space-between"
                    >
                      <Box display="flex" alignItems="center" gap={1}>
                        <Box width={25}>
                          <img src="/newdashboard/performance/material-symbols-light_docs.png" />
                        </Box>
                        <Typography
                          fontFamily="Inter"
                          fontWeight={600}
                          color="#572575"
                          fontSize="20px"
                        >
                          My Exam
                        </Typography>
                      </Box>

                      {dashBoardData?.finalData?.length > 0 && (
                        <ViewAllBlueButton
                          onClick={handleNavigateToTest}
                          showEndIcon
                        />
                      )}
                    </Grid>

                    {dashBoardData?.finalData?.map((item, index) => {
                      const config = progressConfig[item.name];

                      return (
                        <Grid item xs={12} md={6} key={index}>
                          <ProgressBarWithLabel
                            label={item?.name}
                            value={item?.given}
                            max={item?.assigned || 10}
                            color={config?.color || "#000"}
                            icon={<img src={config?.icon} alt="" />}
                            showViewAllButton
                          />
                        </Grid>
                      );
                    })}
                  </Grid>
                </CustomCardWithBorder>
              </Grid>
            ) : (
              <Grid item xs={12}>
                <NoTestTypePurchaseScreen
                  topImage={
                    <img src="/newdashboard/performance/20769812_Sandy_Bus-25_Single-04 1 (1).png" />
                  }
                  text="No Test Type Purchased Yet"
                  subText="You haven't purchased any exam packages yet. Explore available exams to unlock full course content, progress tracking, mock tests, and personalized study insights."
                  buttonName="Explore Test"
                  redirectionLink={redirectionLink1}
                  maxWidth="100%"
                  showHeading="My Exam"
                  headingIcon="/newdashboard/performance/material-symbols-light_docs.png"
                  onClick={() => handleViewTestdetails(testTypeData)}
                />
              </Grid>
            )}
          </>
        )}
      </Grid>
    </>
  );
};

export default PerformanceDashboard;
